import React, { useState } from "react";
import PropTypes from "prop-types";
import { FormControl, IconButton, InputAdornment, InputLabel, makeStyles, MenuItem, OutlinedInput, Select, Typography } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { selectMenuProps } from "../../../../util";
import LoadingSpinner from "../../LoadingSpinner";
import ClearIcon from "@material-ui/icons/Clear";

const useStyles = makeStyles({
  clearButton: {
    display: "none",
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 0.2s ease",
  },
  clearButtonVisible: {
    display: "inline",
    opacity: 1,
    pointerEvents: "auto",
  },
  adornment: {
    marginRight: 16,
  },
});

const SingleSelectDropdown = ({ title, values, onChange, defaultSelected = "", loadingMessage, loading, error = false, emptyMessage, canClear = true }) => {
  const classes = useStyles();
  const [selectedItem, setSelectedItem] = useState(defaultSelected);

  const handleChange = (value) => {
    setSelectedItem(value);
    onChange(value);
  };

  return (
    <FormControl
      fullWidth
      variant="outlined"
      size="small"
    >
      {title && (
        <InputLabel id="single-select-dropdown-label">
          <FormattedMessage id={title} />
        </InputLabel>
      )}

      <Select
        labelId="single-select-dropdown-label"
        id="single-select-dropdown"
        value={selectedItem ?? ""}
        MenuProps={selectMenuProps}
        onChange={(e) => handleChange(e.target.value)}
        input={<OutlinedInput label={<FormattedMessage id={title} />} />}
        endAdornment={
          canClear && (
            <ClearAdornment
              value={selectedItem}
              onClear={() => handleChange("")}
            />
          )
        }
        renderValue={(selected) => {
          if (!selected) return null;
          const selectedOption = values.find(({ value }) => value === selected);
          return selectedOption ? <FormattedMessage id={selectedOption.label} /> : null;
        }}
      >
        {loading && (
          <MenuItem disabled>
            <LoadingSpinner
              message={loadingMessage ? <FormattedMessage id={loadingMessage} /> : null}
              size={24}
            />
          </MenuItem>
        )}

        {error && (
          <MenuItem disabled>
            <FormattedMessage
              id={error}
              defaultMessage={error}
            />
          </MenuItem>
        )}

        {!loading && !error && values.length === 0 && emptyMessage && (
          <MenuItem disabled>
            <Typography component="i">
              <FormattedMessage id={emptyMessage} />
            </Typography>
          </MenuItem>
        )}

        {values.map(({ value, label }) => (
          <MenuItem
            key={value}
            value={value}
          >
            <FormattedMessage id={label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const optionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  label: PropTypes.string.isRequired,
});

SingleSelectDropdown.propTypes = {
  title: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(optionShape).isRequired,
  onChange: PropTypes.func.isRequired,
  defaultSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  loading: PropTypes.bool,
  loadingMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  emptyMessage: PropTypes.string,
};

export default SingleSelectDropdown;

const ClearAdornment = ({ value, onClear }) => {
  const classes = useStyles();

  return (
    <InputAdornment
      position="end"
      className={classes.adornment}
    >
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
        className={`${classes.clearButton} ${value ? classes.clearButtonVisible : ""}`}
      >
        <ClearIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  );
};
